import { useEffect, useState } from 'react';
import type { IdeaDetailResponse, IdeaStatus, IdeaTransitionOption, Role } from '@shared/types';
import { IDEA_STATUS_DESCRIPTIONS, IDEA_STATUS_LABELS } from '@shared/ideaStatus';
import { formatDate, isOverdue, toDateInputValue } from '@shared/format';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Select, TextInput } from '../../../components/Field';
import { Modal } from '../../../overlay';
import { toast } from '../../../components/Toast';
import { api, errorMessage } from '../../../lib/api';

/**
 * 状态条与流转按钮。
 *
 * 主流程只有四步，界面就只画这四步 —— 不做流程引擎的示意；
 * 归档是旁路动作，单独放在右边。
 */
const MAIN_FLOW: readonly IdeaStatus[] = ['submitted', 'validating', 'validated', 'incubating', 'shipped'];

export interface MemberOption {
  id: string;
  name: string;
  role: Role;
}

function StatusStepper({ status }: { status: IdeaStatus }) {
  if (status === 'draft') {
    return (
      <div className="flex items-center gap-2 text-xs text-fg-muted">
        <Badge tone="neutral">草稿</Badge>
        <span>{IDEA_STATUS_DESCRIPTIONS.draft}</span>
      </div>
    );
  }
  if (status === 'archived') {
    return (
      <div className="flex items-center gap-2 text-xs text-fg-muted">
        <Badge tone="neutral">已归档</Badge>
        <span>{IDEA_STATUS_DESCRIPTIONS.archived}</span>
      </div>
    );
  }

  const currentIndex = MAIN_FLOW.indexOf(status);
  return (
    <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      {MAIN_FLOW.map((step, index) => {
        const done = index < currentIndex;
        const active = index === currentIndex;
        return (
          <li key={step} className="flex items-center gap-1.5">
            <span
              className={[
                'rounded px-1.5 py-0.5 text-[11px] font-medium',
                active
                  ? 'bg-accent text-accent-fg'
                  : done
                    ? 'bg-ok-soft text-ok'
                    : 'text-fg-subtle',
              ].join(' ')}
              title={IDEA_STATUS_DESCRIPTIONS[step]}
            >
              {IDEA_STATUS_LABELS[step]}
            </span>
            {index < MAIN_FLOW.length - 1 ? (
              <span aria-hidden className={done ? 'text-ok' : 'text-fg-subtle'}>
                →
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

export function IdeaStatusFlow({
  idea,
  members,
  onUpdated,
}: {
  idea: IdeaDetailResponse;
  members: readonly MemberOption[];
  onUpdated: (updated: IdeaDetailResponse) => void;
}) {
  const [pending, setPending] = useState<IdeaTransitionOption | null>(null);
  const [assigneeId, setAssigneeId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [busy, setBusy] = useState(false);

  // 认领/立项默认选中当前负责人，减少一次点击
  useEffect(() => {
    if (!pending) return;
    setAssigneeId(idea.assigneeId ?? '');
    setDueAt(toDateInputValue(idea.dueAt));
  }, [pending, idea.assigneeId, idea.dueAt]);

  const submit = async (transition: IdeaTransitionOption, assignee?: string, due?: string) => {
    setBusy(true);
    try {
      const updated = await api.post<IdeaDetailResponse>(`/api/ideas/${idea.id}/transition`, {
        to: transition.to,
        assigneeId: assignee,
        dueAt: due === '' ? null : due,
      });
      onUpdated(updated);
      setPending(null);
      toast.ok(`已更新为「${IDEA_STATUS_LABELS[transition.to]}」`);
    } catch (error) {
      toast.critical('状态流转失败', errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const start = (transition: IdeaTransitionOption) => {
    if (transition.requiresAssignee) {
      setPending(transition);
      return;
    }
    void submit(transition);
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <StatusStepper status={idea.status} />

        <div className="flex flex-wrap items-center gap-2">
          {idea.assigneeName ? (
            <span className={['text-[11px]', isOverdue(idea.dueAt) && idea.status === 'incubating' ? 'text-critical' : 'text-fg-muted'].join(' ')}>
              负责人 {idea.assigneeName}
              {idea.dueAt ? ` · 目标 ${formatDate(idea.dueAt)}` : ''}
              {isOverdue(idea.dueAt) && idea.status === 'incubating' ? '（已逾期）' : ''}
            </span>
          ) : null}

          {idea.transitions.map((transition) => (
            <Button
              key={`${transition.from}-${transition.to}`}
              size="sm"
              variant={transition.tone === 'primary' ? 'primary' : transition.tone === 'danger' ? 'danger' : 'default'}
              title={transition.description}
              disabled={busy}
              onClick={() => start(transition)}
            >
              {transition.label}
            </Button>
          ))}
        </div>
      </div>

      <Modal
        open={pending !== null}
        onClose={() => setPending(null)}
        title={pending ? `${pending.label}：指定负责人` : ''}
        description={pending?.description}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPending(null)}>
              取消
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={assigneeId === ''}
              onClick={() => pending && void submit(pending, assigneeId, dueAt)}
            >
              确认
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Select
            label="负责人"
            required
            placeholder="选择一位同事"
            value={assigneeId}
            onChange={(event) => setAssigneeId(event.target.value)}
            options={members.map((member) => ({ value: member.id, label: `${member.name}（${member.role === 'admin' ? '管理员' : member.role === 'reviewer' ? '评审员' : '成员'}）` }))}
          />
          <TextInput
            label="目标时间"
            type="date"
            value={dueAt}
            onChange={(event) => setDueAt(event.target.value)}
            hint="可以留空；填写后会在看板上提示逾期。"
          />
        </div>
      </Modal>
    </>
  );
}
